import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getTheme, type ThemeId } from './themes';

type ThemeContextType = {
  themeId: ThemeId;
  setThemeId: (id: ThemeId) => void;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useThemeId = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeId must be used within ThemeProvider');
  return ctx;
};

const STORAGE_KEY = 'mailing_constructor_theme';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeId, setThemeIdState] = useState<ThemeId>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'smsby' || stored === 'dataedge') return stored;
    } catch {
      /* ignore */
    }
    return 'smsby';
  });

  const setThemeId = (id: ThemeId) => {
    setThemeIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  };

  const theme = useMemo(() => getTheme(themeId), [themeId]);
  const value = useMemo(() => ({ themeId, setThemeId }), [themeId]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
